import {useEffect, useState} from 'react';

/**
 * Shared duration for counting figures and the bars that fill alongside them, so a
 * number and its meter always land together.
 */
export const COUNT_UP_DURATION_MS = 1800;

/** Honours the OS "reduce motion" setting; treated as false where matchMedia is absent (SSR). */
const prefersReducedMotion = () =>
    globalThis.window !== undefined && globalThis.window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/**
 * Counts from 0 up to `target` once `active` turns true, on a cubic ease-out.
 *
 * Pair it with `useInView` so the count runs when the figure scrolls into view
 * rather than on mount. The returned value starts at 0, so the final figure is a
 * client-side enhancement — keep the number itself out of anything that has to be
 * correct in the server HTML.
 *
 * Readers who ask for reduced motion get the final figure immediately.
 *
 * Pass a changing `runKey` to replay the count — the animation restarts whenever it
 * changes, which a one-shot `active` flag cannot do on its own.
 */
export function useCountUp(target: number, decimals: number, active: boolean, duration = COUNT_UP_DURATION_MS, runKey = 0) {
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!active) return undefined;

        if (prefersReducedMotion()) {
            setCount(target);
            return undefined;
        }

        let frame = 0;
        let start: number | null = null;
        const step = (timestamp: number) => {
            if (!start) start = timestamp;
            const progress = Math.min((timestamp - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Number.parseFloat((eased * target).toFixed(decimals)));
            if (progress < 1) frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);

        // Without this the loop keeps calling setCount after an unmount mid-animation.
        return () => cancelAnimationFrame(frame);
    }, [active, target, decimals, duration, runKey]);

    return count;
}
